import { useState, useMemo, type Dispatch, type SetStateAction } from 'react';
import { EmbedConfig, EmbedProductConfig } from '../../utils/urlGenerator';
import ProductMultiSelect from './ProductMultiSelect';
import EmbedCodeDisplay from './EmbedCodeDisplay';
import ConfigPreview from './ConfigPreview';
import MultiSelect, { MultiSelectOption } from './MultiSelect';
import { useGetProductQuery } from '../../../api';
import { buildDashboardStateUrl } from '../../utils/dashboardState';
import ImportSettingsModal from './ImportSettingsModal';
import type { ImportParseResult } from '../../utils/importSnippetParser';

interface ShopifyVariantInfo {
  shopifyProductId: string;
  shopifyProductTitle: string;
  description: string;
  descriptionHtml: string;
  priceRange: {
    minPrice: string;
    maxPrice: string;
    currencyCode: string;
    compareAtPriceMin?: string;
  };
  productTag: string;
  variants: Array<{
    variantId: string;
    variantTitle: string;
    sku: string;
  }>;
}

/**
 * Main Dashboard Layout
 *
 * Two-column layout:
 * - Left third: Customization form inputs
 * - Right two-thirds: Live preview (top 3/4) + Code display (bottom 1/4)
 *
 * DashboardMain is only mounted after shopifyVariants is fully loaded (Dashboard.tsx
 * owns the loading gate), so config can be initialised synchronously on first render.
 */

interface DashboardMainProps {
  partnerKey: string;
  partnerId?: string;
  affiliateRfsn?: string;
  onBack: () => void;
  shopifyVariants: Map<string, ShopifyVariantInfo>;
  initialConfig?: EmbedConfig;
  initialConfiguringProductTag?: string;
}

type PendingHeaderAction = 'changePartner' | 'fullscreen' | null;

type AppearanceColorKey =
  | 'brandBannerColor'
  | 'accentColor'
  | 'primaryButtonColor'
  | 'primaryButtonText'
  | 'secondaryButtonColor'
  | 'secondaryButtonText'
  | 'headerBgColor'
  | 'bodyBgColor'
  | 'borderColor'
  | 'textColor'
  | 'secondaryTextColor';

function ResetColorButton({
  onClick,
  label
}: {
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={`Reset ${label} to default`}
      aria-label={`Reset ${label} to default`}
      className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-300 text-gray-500 transition hover:border-gray-400 hover:text-[#014876] focus:outline-none focus:ring-2 focus:ring-[#014876]"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M3 12a9 9 0 1 0 3-6.7" />
        <path d="M3 3v6h6" />
      </svg>
    </button>
  );
}

function AppearanceColorField({
  label,
  helpText,
  value,
  setValue,
  defaultValue,
  configKey,
  setConfig
}: {
  label: string;
  helpText: string;
  value: string;
  setValue: (value: string) => void;
  defaultValue: string;
  configKey: AppearanceColorKey;
  setConfig: Dispatch<SetStateAction<EmbedConfig>>;
}) {
  return (
    <div className="mb-4">
      <label className="mb-1 block text-sm font-semibold text-[#014876]">
        {label}
      </label>
      <p className="mb-2 text-xs text-gray-500">{helpText}</p>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value}
          onChange={e => {
            const val = e.target.value;
            setValue(val);
            setConfig(prev => ({
              ...prev,
              [configKey]: val === defaultValue ? undefined : val
            }));
          }}
          className="h-9 w-10 cursor-pointer rounded border border-gray-300 p-0.5"
        />
        <input
          type="text"
          value={value}
          onChange={e => {
            const val = e.target.value;
            setValue(val);
            if (/^#[0-9a-fA-F]{6}$/.test(val)) {
              setConfig(prev => ({
                ...prev,
                [configKey]: val === defaultValue ? undefined : val
              }));
            }
          }}
          className="w-28 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#014876] focus:outline-none"
          placeholder={defaultValue}
          maxLength={7}
        />
        <ResetColorButton
          label={label.toLowerCase()}
          onClick={() => {
            setValue(defaultValue);
            setConfig(prev => ({
              ...prev,
              [configKey]: undefined
            }));
          }}
        />
      </div>
    </div>
  );
}
// const isDev = false;
// const BASE_URL = isDev ? 'http://localhost:5174/embed.html' : 'https://sandbox-a.customer.namebubbles-dev.com/embed.html' ; // FOR DEV TODO: Update to correct environment
// const BASE_URL = "https://namebubbles.com/embed-config.html"; // FOR PROD

function buildVariantConfig(
  productTag: string,
  shopifyVariants: Map<string, ShopifyVariantInfo>
): Partial<EmbedConfig> {
  const v = shopifyVariants.get(productTag);
  if (!v) return {};
  return {
    shopifyVariantId: v.variants[0]?.variantId,
    shopifyDomain: 'namebubbles.com',
    productTitle: v.shopifyProductTitle,
    productPriceMin: v.priceRange.minPrice,
    productPriceMax: v.priceRange.maxPrice,
    productCurrency: v.priceRange.currencyCode,
    productCompareAtPriceMin: v.priceRange.compareAtPriceMin
  };
}

function buildProductConfig(
  productTag: string,
  displayName: string,
  shopifyVariants: Map<string, ShopifyVariantInfo>,
  overrides: Partial<EmbedProductConfig> = {}
): EmbedProductConfig {
  const v = shopifyVariants.get(productTag);
  return {
    productTag,
    productTitle: v?.shopifyProductTitle ?? displayName,
    shopifyVariantId: v?.variants[0]?.variantId,
    shopifyDomain: v ? 'namebubbles.com' : undefined,
    productPriceMin: v?.priceRange.minPrice,
    productPriceMax: v?.priceRange.maxPrice,
    productCurrency: v?.priceRange.currencyCode,
    productCompareAtPriceMin: v?.priceRange.compareAtPriceMin,
    allowedStyles: [],
    allowedFonts: [],
    ...overrides
  };
}

function buildInitialDashboardConfig(
  firstTag: string,
  partnerKey: string,
  affiliateRfsn: string | undefined,
  shopifyVariants: Map<string, ShopifyVariantInfo>,
  initialConfig?: EmbedConfig
): EmbedConfig {
  const fallbackVariantInfo = buildVariantConfig(firstTag, shopifyVariants);
  const fallbackProduct = buildProductConfig(
    firstTag,
    firstTag,
    shopifyVariants,
    {
      isDefault: true
    }
  );

  if (!initialConfig) {
    return {
      productTag: firstTag,
      partnerKey,
      affiliateRfsn,
      allowedStyles: [],
      allowedFonts: [],
      width: '1200',
      height: '800',
      ...fallbackVariantInfo,
      products: firstTag ? [fallbackProduct] : []
    };
  }

  const sourceProducts =
    initialConfig.products && initialConfig.products.length > 0
      ? initialConfig.products
      : initialConfig.productTag
      ? [
          {
            productTag: initialConfig.productTag,
            shopifyVariantId: initialConfig.shopifyVariantId,
            shopifyDomain: initialConfig.shopifyDomain,
            productTitle: initialConfig.productTitle,
            productPriceMin: initialConfig.productPriceMin,
            productPriceMax: initialConfig.productPriceMax,
            productCurrency: initialConfig.productCurrency,
            productCompareAtPriceMin: initialConfig.productCompareAtPriceMin,
            allowedStyles: initialConfig.allowedStyles,
            allowedColors: initialConfig.allowedColors,
            allowedFonts: initialConfig.allowedFonts,
            allowedIcons: initialConfig.allowedIcons,
            allowedMaterials: initialConfig.allowedMaterials,
            isDefault: true
          }
        ]
      : [];

  const products = sourceProducts.map((product, index) =>
    buildProductConfig(
      product.productTag,
      product.productTitle ?? product.productTag,
      shopifyVariants,
      {
        ...product,
        isDefault: product.isDefault ?? index === 0
      }
    )
  );

  const defaultProduct =
    products.find(product => product.isDefault) ?? products[0];
  const defaultProductTag =
    defaultProduct?.productTag ?? initialConfig.productTag ?? firstTag;
  const defaultVariantInfo = defaultProduct
    ? buildVariantConfig(defaultProduct.productTag, shopifyVariants)
    : fallbackVariantInfo;

  return {
    ...initialConfig,
    partnerKey,
    affiliateRfsn,
    productTag: defaultProductTag,
    width: initialConfig.width ?? '1200',
    height: initialConfig.height ?? '800',
    allowedStyles:
      products.length <= 1
        ? products[0]?.allowedStyles ?? initialConfig.allowedStyles ?? []
        : initialConfig.allowedStyles ?? [],
    allowedFonts:
      products.length <= 1
        ? products[0]?.allowedFonts ?? initialConfig.allowedFonts ?? []
        : initialConfig.allowedFonts ?? [],
    ...defaultVariantInfo,
    products: defaultProductTag ? products : []
  };
}

function ConfirmationModal({
  title,
  description,
  confirmLabel,
  onCancel,
  onConfirm
}: {
  title: string;
  description: string;
  confirmLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
        <h2 className="mb-2 text-lg font-semibold text-[#014876]">{title}</h2>
        <p className="mb-6 text-sm leading-6 text-gray-600">{description}</p>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="rounded-lg bg-[#014876] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#013456]"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DashboardMain({
  partnerKey,
  partnerId,
  affiliateRfsn,
  onBack,
  shopifyVariants,
  initialConfig,
  initialConfiguringProductTag
}: DashboardMainProps) {
  const firstTag = useMemo(
    () => [...shopifyVariants.keys()][0] ?? '',
    [shopifyVariants]
  );

  // Tracks which product's style/font restrictions are shown in the form.
  const [configuringProductTag, setConfiguringProductTag] = useState(
    initialConfiguringProductTag ?? initialConfig?.productTag ?? firstTag
  );

  // Initialised once — shopifyVariants is guaranteed populated when this mounts.
  const [config, setConfig] = useState<EmbedConfig>(() => ({
    ...buildInitialDashboardConfig(
      firstTag,
      partnerKey,
      affiliateRfsn,
      shopifyVariants,
      initialConfig
    ),
    partnerId
  }));
  const [pendingHeaderAction, setPendingHeaderAction] =
    useState<PendingHeaderAction>(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  // Enrich newly added products with Shopify variant data, keep configuringProductTag valid,
  // and sync flat config fields for the single-product URL path (task 2.7).
  const handleProductsChange = (products: EmbedProductConfig[]) => {
    const enriched = products.map(p => {
      if (p.shopifyVariantId) return p;
      return buildProductConfig(
        p.productTag,
        p.productTitle ?? p.productTag,
        shopifyVariants,
        {
          isDefault: p.isDefault,
          allowedStyles: p.allowedStyles,
          allowedFonts: p.allowedFonts
        }
      );
    });

    const tags = enriched.map(p => p.productTag);
    const nextConfiguring = tags.includes(configuringProductTag)
      ? configuringProductTag
      : enriched[0]?.productTag ?? '';
    if (nextConfiguring !== configuringProductTag) {
      setConfiguringProductTag(nextConfiguring);
    }

    const defaultProduct = enriched.find(p => p.isDefault) ?? enriched[0];
    const flatVariantInfo = defaultProduct
      ? buildVariantConfig(defaultProduct.productTag, shopifyVariants)
      : {};

    setConfig(prev => ({
      ...prev,
      products: enriched,
      productTag: defaultProduct?.productTag ?? prev.productTag,
      ...flatVariantInfo,
      // Single-product: keep flat style/font fields in sync (task 2.7)
      allowedStyles:
        enriched.length <= 1
          ? enriched[0]?.allowedStyles ?? []
          : prev.allowedStyles,
      allowedFonts:
        enriched.length <= 1
          ? enriched[0]?.allowedFonts ?? []
          : prev.allowedFonts
    }));
  };

  // Updates allowedStyles / allowedFonts on the currently-configuring product.
  const updateConfiguringProduct = (updates: Partial<EmbedProductConfig>) => {
    setConfig(prev => {
      const products = (prev.products ?? []).map(p =>
        p.productTag === configuringProductTag ? { ...p, ...updates } : p
      );
      // Also keep flat fields synced for single-product URL generation
      const flatUpdates = products.length <= 1 ? updates : {};
      return { ...prev, products, ...flatUpdates };
    });
  };

  // The EmbedProductConfig entry for the product currently being configured.
  const configuringProduct = useMemo(
    () => config.products?.find(p => p.productTag === configuringProductTag),
    [config.products, configuringProductTag]
  );

  // Memoize query args so RTK Query gets a stable object reference between renders
  const productQueryArgs = useMemo(
    () => ({ productTag: configuringProductTag, partnerId }),
    [configuringProductTag, partnerId]
  );

  // Fetch product details to populate filter options (RTK Query caches per tag).
  const { data: productData, isLoading: productLoading } = useGetProductQuery(
    productQueryArgs,
    { skip: !configuringProductTag }
  );

  // Convert product data to multi-select options
  const styleOptions: MultiSelectOption[] = useMemo(
    () =>
      productData?.styles?.map(style => ({
        id: style.style_id,
        label: style.display_name
      })) || [],
    [productData]
  );

  const fontOptions: MultiSelectOption[] = useMemo(() => {
    if (!productData?.fonts || !productData?.styles) return [];

    // Only show fonts that are available for the currently allowed styles
    const allowedStyleIds = configuringProduct?.allowedStyles ?? [];

    // If no styles are selected, show all fonts
    if (allowedStyleIds.length === 0) {
      return productData.fonts.map(font => ({
        id: font.font_id,
        label: font.display_name
      }));
    }

    // Get font IDs from allowed styles
    const allowedStyles = productData.styles.filter(style =>
      allowedStyleIds.includes(style.style_id)
    );
    const allowedFontIds = new Set(
      allowedStyles.flatMap(style => style.font_ids || [])
    );

    // Filter fonts to only those available in allowed styles
    return productData.fonts
      .filter(font => allowedFontIds.has(font.font_id))
      .map(font => ({
        id: font.font_id,
        label: font.display_name
      }));
  }, [productData, configuringProduct?.allowedStyles]);

  const DEFAULT_BRAND_BANNER_COLOR = '#014876';
  const DEFAULT_ACCENT_COLOR = '#014876';
  const DEFAULT_PRIMARY_BUTTON_COLOR = '#014876';
  const DEFAULT_PRIMARY_BUTTON_TEXT = '#ffffff';
  const DEFAULT_SECONDARY_BUTTON_COLOR = '#ffffff';
  const DEFAULT_SECONDARY_BUTTON_TEXT = '#014876';
  const DEFAULT_HEADER_BG_COLOR = '#f9fafb';
  const DEFAULT_BODY_BG_COLOR = '#ffffff';
  const DEFAULT_BORDER_COLOR = '#e5e7eb';
  const DEFAULT_TEXT_COLOR = '#424242';
  const DEFAULT_SECONDARY_TEXT_COLOR = '#6b7280';
  const [brandBannerColorValue, setBrandBannerColorValue] = useState(
    config.brandBannerColor ?? DEFAULT_BRAND_BANNER_COLOR
  );
  const [accentColorValue, setAccentColorValue] = useState(
    config.accentColor ?? DEFAULT_ACCENT_COLOR
  );
  const [primaryButtonColorValue, setPrimaryButtonColorValue] = useState(
    config.primaryButtonColor ?? DEFAULT_PRIMARY_BUTTON_COLOR
  );
  const [primaryButtonTextValue, setPrimaryButtonTextValue] = useState(
    config.primaryButtonText ?? DEFAULT_PRIMARY_BUTTON_TEXT
  );
  const [secondaryButtonColorValue, setSecondaryButtonColorValue] = useState(
    config.secondaryButtonColor ?? DEFAULT_SECONDARY_BUTTON_COLOR
  );
  const [secondaryButtonTextValue, setSecondaryButtonTextValue] = useState(
    config.secondaryButtonText ?? DEFAULT_SECONDARY_BUTTON_TEXT
  );
  const [headerBgColorValue, setHeaderBgColorValue] = useState(
    config.headerBgColor ?? DEFAULT_HEADER_BG_COLOR
  );
  const [bodyBgColorValue, setBodyBgColorValue] = useState(
    config.bodyBgColor ?? DEFAULT_BODY_BG_COLOR
  );
  const [borderColorValue, setBorderColorValue] = useState(
    config.borderColor ?? DEFAULT_BORDER_COLOR
  );
  const [textColorValue, setTextColorValue] = useState(
    config.textColor ?? DEFAULT_TEXT_COLOR
  );
  const [secondaryTextColorValue, setSecondaryTextColorValue] = useState(
    config.secondaryTextColor ?? DEFAULT_SECONDARY_TEXT_COLOR
  );

  const openPartnerDashboardApp = () => {
    const currentDashboardUrl = `${window.location.origin}${window.location.pathname}`;
    const fullscreenUrl = buildDashboardStateUrl(currentDashboardUrl, {
      partnerKey,
      affiliateRfsn,
      configuringProductTag,
      config: {
        ...config,
        partnerKey,
        affiliateRfsn
      }
    });
    window.open(fullscreenUrl, '_blank', 'noopener,noreferrer');
  };

  const handleConfirmHeaderAction = () => {
    if (pendingHeaderAction === 'changePartner') {
      onBack();
    }

    if (pendingHeaderAction === 'fullscreen') {
      openPartnerDashboardApp();
    }

    setPendingHeaderAction(null);
  };

  const handleImport = (result: ImportParseResult) => {
    const isMismatch =
      !!result.extractedPartnerKey && result.extractedPartnerKey !== partnerKey;

    // On partner key mismatch, restrict to appearance/cosmetic fields only.
    // Merging the other partner's products, partnerId, or filter arrays causes
    // 404s and shows products the current partner cannot access.
    const configToMerge: Partial<EmbedConfig> = isMismatch
      ? {
          fontFamily: result.config.fontFamily,
          customFontClass: result.config.customFontClass,
          brandBannerColor: result.config.brandBannerColor,
          accentColor: result.config.accentColor,
          primaryButtonColor: result.config.primaryButtonColor,
          primaryButtonText: result.config.primaryButtonText,
          secondaryButtonColor: result.config.secondaryButtonColor,
          secondaryButtonText: result.config.secondaryButtonText,
          headerBgColor: result.config.headerBgColor,
          bodyBgColor: result.config.bodyBgColor,
          borderColor: result.config.borderColor,
          textColor: result.config.textColor,
          secondaryTextColor: result.config.secondaryTextColor,
          brandName: result.config.brandName
        }
      : result.config;

    setConfig(prev => ({
      ...prev,
      ...configToMerge,
      ...(result.width ? { width: result.width } : {}),
      ...(result.height ? { height: result.height } : {})
    }));

    // Sync the color picker controlled states — each color field has its own
    // separate useState var that drives the input, so a setConfig alone won't
    // update the visible picker value.
    const c = configToMerge;
    if (c.brandBannerColor) setBrandBannerColorValue(c.brandBannerColor);
    if (c.accentColor) setAccentColorValue(c.accentColor);
    if (c.primaryButtonColor) setPrimaryButtonColorValue(c.primaryButtonColor);
    if (c.primaryButtonText) setPrimaryButtonTextValue(c.primaryButtonText);
    if (c.secondaryButtonColor)
      setSecondaryButtonColorValue(c.secondaryButtonColor);
    if (c.secondaryButtonText)
      setSecondaryButtonTextValue(c.secondaryButtonText);
    if (c.headerBgColor) setHeaderBgColorValue(c.headerBgColor);
    if (c.bodyBgColor) setBodyBgColorValue(c.bodyBgColor);
    if (c.borderColor) setBorderColorValue(c.borderColor);
    if (c.textColor) setTextColorValue(c.textColor);
    if (c.secondaryTextColor) setSecondaryTextColorValue(c.secondaryTextColor);

    setIsImportModalOpen(false);
  };

  const hasProducts = (config.products?.length ?? 0) > 0;
  const isMultiProduct = (config.products?.length ?? 0) >= 2;

  return (
    <div
      className="min-h-screen bg-[#DFEDED]"
      style={{ fontFamily: 'Poppins, sans-serif' }}
    >
      {/* Header */}
      <header className="bg-[#014876] text-white shadow-lg">
        <div className="mx-auto flex max-w-[2560px] items-center justify-between px-6 py-3">
          <div>
            <h1 className="text-xl font-semibold">
              NameBubbles Partner Dashboard
            </h1>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setIsImportModalOpen(true)}
              className="rounded-lg bg-white px-4 py-2 text-sm font-semibold text-[#014876] transition hover:bg-gray-100"
            >
              Import Settings
            </button>
            <button
              onClick={() => setPendingHeaderAction('changePartner')}
              className="rounded-lg bg-white px-4 py-2 text-sm font-semibold text-[#014876] transition hover:bg-gray-100"
            >
              Change Affiliate ID
            </button>
            <button
              onClick={() => setPendingHeaderAction('fullscreen')}
              className="flex flex-row-reverse items-center justify-around gap-1 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-[#014876] transition hover:bg-gray-100"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polyline points="15 3 21 3 21 9"></polyline>
                <polyline points="9 21 3 21 3 15"></polyline>
                <line x1="21" y1="3" x2="14" y2="10"></line>
                <line x1="3" y1="21" x2="10" y2="14"></line>
              </svg>
              Fullscreen
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="mx-auto max-w-[2560px] p-4">
        <div className="flex flex-col gap-4 md:h-[calc(100vh-102px)] md:flex-row">
          {/* Left Third - Customization Form + Embed Code */}
          <div className="flex w-full flex-col gap-4  md:w-1/3 md:max-w-[650px]">
            {/* Scrollable form area */}
            <div className="min-h-0 overflow-hidden rounded-lg bg-white p-4 shadow-lg [flex:2_1_0%]">
              <div className="h-full overflow-y-auto p-1">
                <h2 className="text-l mb-1 font-semibold text-[#014876]">
                  Customize Your Widget
                </h2>

                {/* Product Multi-Selection */}
                <ProductMultiSelect
                  value={config.products ?? []}
                  onChange={handleProductsChange}
                  partnerKey={partnerKey}
                />

                {/* Currently Configuring — shown only when 2+ products are selected */}
                {isMultiProduct && (
                  <div className="mb-6">
                    <label className="mb-2 block text-sm font-semibold text-[#014876]">
                      Currently Configuring
                    </label>
                    <p className="mb-2 text-xs text-gray-500">
                      Select which product's style and font restrictions to
                      configure below
                    </p>
                    <select
                      value={configuringProductTag}
                      onChange={e => setConfiguringProductTag(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#014876] focus:outline-none"
                    >
                      {config.products?.map(p => (
                        <option key={p.productTag} value={p.productTag}>
                          {p.isDefault ? '★ ' : ''}
                          {p.productTitle ?? p.productTag}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Styles Filter */}
                {hasProducts && (
                  <MultiSelect
                    label="Allowed Styles"
                    options={styleOptions}
                    selectedIds={configuringProduct?.allowedStyles ?? []}
                    onChange={selectedIds =>
                      updateConfiguringProduct({ allowedStyles: selectedIds })
                    }
                    placeholder="All styles allowed"
                    helpText="Deselect styles to exclude them from your embed"
                    disabled={productLoading}
                  />
                )}

                {/* Fonts Filter */}
                {hasProducts && (
                  <MultiSelect
                    label="Allowed Fonts"
                    options={fontOptions}
                    selectedIds={configuringProduct?.allowedFonts ?? []}
                    onChange={selectedIds =>
                      updateConfiguringProduct({ allowedFonts: selectedIds })
                    }
                    placeholder="All fonts allowed"
                    helpText="Deselect fonts to exclude them from your embed"
                    disabled={productLoading}
                  />
                )}

                {/* Embed Dimensions */}
                <div className="mb-6">
                  <label className="mb-2 block text-sm font-semibold text-[#014876]">
                    Embed Width (px)
                  </label>
                  <p className="mb-2 text-xs text-gray-500">Width in pixels</p>
                  <input
                    type="number"
                    value={config.width || ''}
                    onChange={e =>
                      setConfig(prev => ({ ...prev, width: e.target.value }))
                    }
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#014876] focus:outline-none"
                    placeholder="1200"
                    min="200"
                  />
                </div>

                <div className="mb-6">
                  <label className="mb-2 block text-sm font-semibold text-[#014876]">
                    Embed Height (px)
                  </label>
                  <p className="mb-2 text-xs text-gray-500">Height in pixels</p>
                  <input
                    type="number"
                    value={config.height || ''}
                    onChange={e =>
                      setConfig(prev => ({ ...prev, height: e.target.value }))
                    }
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#014876] focus:outline-none"
                    placeholder="800"
                    min="400"
                  />
                </div>

                {/* Embed Appearance */}
                <div className="border-t border-gray-200 pt-4">
                  <h3 className="mb-4 text-sm font-semibold text-[#014876]">
                    Embed Appearance
                  </h3>

                  {/* Brand Name */}
                  <div className="mb-4">
                    <label className="mb-1 block text-sm font-semibold text-[#014876]">
                      Brand Name
                    </label>
                    <p className="mb-2 text-xs text-gray-500">
                      Displays a collaboration header above the product title
                      (e.g. "Acme x Name Bubbles")
                    </p>
                    <input
                      type="text"
                      value={config.brandName ?? ''}
                      onChange={e =>
                        setConfig(prev => ({
                          ...prev,
                          brandName: e.target.value || undefined
                        }))
                      }
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#014876] focus:outline-none"
                      placeholder="Your Brand Name"
                    />
                  </div>

                  {/* Brand Font */}
                  <div className="mb-4">
                    <label className="mb-1 block text-sm font-semibold text-[#014876]">
                      Brand Font
                    </label>
                    <p className="mb-2 text-xs text-gray-500">
                      Choose a font for your embed widget
                    </p>
                    <select
                      value={config.fontFamily ?? 'none'}
                      onChange={e => {
                        const val = e.target.value;
                        setConfig(prev => ({
                          ...prev,
                          fontFamily: val === 'none' ? undefined : val,
                          customFontClass:
                            val === 'none' || val !== 'custom'
                              ? undefined
                              : prev.customFontClass
                        }));
                      }}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#014876] focus:outline-none"
                    >
                      <option value="none">None / Default (Poppins)</option>
                      <optgroup label="Sans-serif">
                        <option value="Inter">Inter</option>
                        <option value="Lato">Lato</option>
                        <option value="Montserrat">Montserrat</option>
                        <option value="Nunito">Nunito</option>
                        <option value="Raleway">Raleway</option>
                        <option value="Open Sans">Open Sans</option>
                        <option value="DM Sans">DM Sans</option>
                      </optgroup>
                      <optgroup label="Serif">
                        <option value="Playfair Display">
                          Playfair Display
                        </option>
                        <option value="Merriweather">Merriweather</option>
                        <option value="Lora">Lora</option>
                      </optgroup>
                      <optgroup label="Display">
                        <option value="Pacifico">Pacifico</option>
                        <option value="Fredoka One">Fredoka One</option>
                      </optgroup>
                      <optgroup label="Custom">
                        <option value="custom">Custom (enter CSS class)</option>
                      </optgroup>
                    </select>
                  </div>

                  {/* CSS Class Name — only shown when "Custom" is selected */}
                  {config.fontFamily === 'custom' && (
                    <div className="mb-4">
                      <label className="mb-1 block text-sm font-semibold text-[#014876]">
                        CSS Class Name
                      </label>
                      <input
                        type="text"
                        value={config.customFontClass ?? ''}
                        onChange={e =>
                          setConfig(prev => ({
                            ...prev,
                            customFontClass: e.target.value || undefined
                          }))
                        }
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#014876] focus:outline-none"
                        placeholder="my-brand-font"
                      />
                    </div>
                  )}

                  <AppearanceColorField
                    label="Brand Banner Color"
                    helpText="Background color for the collaboration banner shown above the product title"
                    value={brandBannerColorValue}
                    setValue={setBrandBannerColorValue}
                    defaultValue={DEFAULT_BRAND_BANNER_COLOR}
                    configKey="brandBannerColor"
                    setConfig={setConfig}
                  />

                  <AppearanceColorField
                    label="Accent Color"
                    helpText="Active and selection indicators, active step headers, and pagination controls"
                    value={accentColorValue}
                    setValue={setAccentColorValue}
                    defaultValue={DEFAULT_ACCENT_COLOR}
                    configKey="accentColor"
                    setConfig={setConfig}
                  />

                  <AppearanceColorField
                    label="Primary Button Color"
                    helpText="Background color for filled primary buttons like Add to Cart, Apply, and Checkout"
                    value={primaryButtonColorValue}
                    setValue={setPrimaryButtonColorValue}
                    defaultValue={DEFAULT_PRIMARY_BUTTON_COLOR}
                    configKey="primaryButtonColor"
                    setConfig={setConfig}
                  />

                  <AppearanceColorField
                    label="Primary Button Text"
                    helpText="Text color for filled primary buttons"
                    value={primaryButtonTextValue}
                    setValue={setPrimaryButtonTextValue}
                    defaultValue={DEFAULT_PRIMARY_BUTTON_TEXT}
                    configKey="primaryButtonText"
                    setConfig={setConfig}
                  />

                  <AppearanceColorField
                    label="Secondary Button Color"
                    helpText="Background color for secondary buttons like Back, See All, and Create Another Label Set"
                    value={secondaryButtonColorValue}
                    setValue={setSecondaryButtonColorValue}
                    defaultValue={DEFAULT_SECONDARY_BUTTON_COLOR}
                    configKey="secondaryButtonColor"
                    setConfig={setConfig}
                  />

                  <AppearanceColorField
                    label="Secondary Button Text"
                    helpText="Text and border color for secondary buttons"
                    value={secondaryButtonTextValue}
                    setValue={setSecondaryButtonTextValue}
                    defaultValue={DEFAULT_SECONDARY_BUTTON_TEXT}
                    configKey="secondaryButtonText"
                    setConfig={setConfig}
                  />

                  <AppearanceColorField
                    label="Header Bar Background"
                    helpText="Background color for the product info"
                    value={headerBgColorValue}
                    setValue={setHeaderBgColorValue}
                    defaultValue={DEFAULT_HEADER_BG_COLOR}
                    configKey="headerBgColor"
                    setConfig={setConfig}
                  />

                  <AppearanceColorField
                    label="Body Background"
                    helpText="Background color for the main content area"
                    value={bodyBgColorValue}
                    setValue={setBodyBgColorValue}
                    defaultValue={DEFAULT_BODY_BG_COLOR}
                    configKey="bodyBgColor"
                    setConfig={setConfig}
                  />

                  <AppearanceColorField
                    label="Border/Divider Color"
                    helpText="Color for borders and dividers"
                    value={borderColorValue}
                    setValue={setBorderColorValue}
                    defaultValue={DEFAULT_BORDER_COLOR}
                    configKey="borderColor"
                    setConfig={setConfig}
                  />

                  <AppearanceColorField
                    label="Primary Text Color"
                    helpText="General non-button text"
                    value={textColorValue}
                    setValue={setTextColorValue}
                    defaultValue={DEFAULT_TEXT_COLOR}
                    configKey="textColor"
                    setConfig={setConfig}
                  />

                  <AppearanceColorField
                    label="Secondary Text Color"
                    helpText="Inactive tab labels and muted/helper text colors"
                    value={secondaryTextColorValue}
                    setValue={setSecondaryTextColorValue}
                    defaultValue={DEFAULT_SECONDARY_TEXT_COLOR}
                    configKey="secondaryTextColor"
                    setConfig={setConfig}
                  />
                </div>
              </div>
            </div>

            {/* Embed Code - anchored to bottom of left panel */}
            <div className="min-h-0 flex-1 overflow-hidden border-t border-gray-200">
              <EmbedCodeDisplay config={config} />
            </div>
          </div>

          {/* Right Two-Thirds - Live Preview (full height) */}
          <div className="flex w-full flex-col md:w-2/3 md:grow md:overflow-hidden">
            <div className="flex h-[480px] flex-col overflow-hidden rounded-lg bg-white shadow-lg md:h-auto md:min-h-0 md:flex-1">
              <h2 className="text-l px-4 py-4 font-semibold text-[#014876]">
                Live Preview
              </h2>
              <div className="flex-1 overflow-hidden p-4 md:min-h-0">
                <ConfigPreview
                  config={config}
                  configuringProductTag={configuringProductTag}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <ImportSettingsModal
        isOpen={isImportModalOpen}
        currentPartnerKey={partnerKey}
        onClose={() => setIsImportModalOpen(false)}
        onImport={handleImport}
      />

      {pendingHeaderAction === 'changePartner' && (
        <ConfirmationModal
          title="Change affiliate ID?"
          description="Changing your affiliate ID will reset your current Embeddable Shop selections and take you back to the affiliate ID entry screen."
          confirmLabel="Reset and Continue"
          onCancel={() => setPendingHeaderAction(null)}
          onConfirm={handleConfirmHeaderAction}
        />
      )}

      {pendingHeaderAction === 'fullscreen' && (
        <ConfirmationModal
          title="Open fullscreen dashboard?"
          description="This will open the dashboard in a new tab. Please note that popup blockers or refreshing the page may cause some selections to be lost."
          confirmLabel="Open Fullscreen"
          onCancel={() => setPendingHeaderAction(null)}
          onConfirm={handleConfirmHeaderAction}
        />
      )}
    </div>
  );
}
